/**
 * FunnelPopup
 *
 * Bottom-right slide-up toast for homepage funnel experiences.
 * Auto-dismisses after 10 seconds.
 * Converted from Emotion styled-components to Tailwind CSS.
 */

import { useEffect, useRef } from 'react'
import { DialogPortal } from '../../components/shared/DialogPortal'
import type { FunnelOffer } from '../funnel-runtime.types'

const AUTO_DISMISS_MS = 10_000

interface FunnelPopupProps {
  offer: FunnelOffer
  onAccept: () => void
  onDecline: () => void
  onDismiss: () => void
  accepting?: boolean
}

export function FunnelPopup({ offer, onAccept, onDecline, onDismiss, accepting }: FunnelPopupProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    timerRef.current = setTimeout(onDismiss, AUTO_DISMISS_MS)
    return () => { clearTimeout(timerRef.current) }
  }, [onDismiss])

  return (
    <DialogPortal>
      <div className="fixed bottom-6 right-6 z-[1500] w-[340px] max-w-[calc(100vw-48px)] overflow-hidden rounded-xl bg-tf-bg-card shadow-[0_8px_32px_rgba(0,0,0,0.15)] animate-funnel-popup-slide-up">
        {/* Close Button */}
        <button
          className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full border-none bg-black/40 text-base leading-none text-white cursor-pointer hover:bg-black/60"
          onClick={onDismiss}
          aria-label="Close"
        >
          &times;
        </button>

        {/* Image */}
        {offer.imageUrl && (
          <img
            src={offer.imageUrl}
            alt={offer.headline}
            className="h-[120px] w-full object-cover"
          />
        )}

        {/* Body */}
        <div className="p-4">
          <h4 className="mb-1.5 text-base font-bold text-tf-text">
            {offer.headline}
          </h4>

          {/* Price Row */}
          <div className="mb-3 flex items-baseline gap-2">
            <span className="text-xl font-extrabold text-tf-text">
              ${offer.pricingValue.toFixed(2)}
            </span>
            <span className="text-sm text-tf-text-sub line-through">
              ${offer.originalPrice.toFixed(2)}
            </span>
          </div>

          {/* CTA Button */}
          <button
            className="w-full rounded-lg border-none bg-emerald-500 px-2.5 py-2.5 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            onClick={onAccept}
            disabled={accepting}
          >
            {accepting ? 'Adding...' : offer.ctaText}
          </button>

          {/* Decline Link */}
          <button
            className="mx-auto mt-2 block border-none bg-transparent text-xs text-tf-text-sub underline cursor-pointer hover:text-tf-text"
            onClick={onDecline}
          >
            {offer.declineText}
          </button>
        </div>
      </div>
    </DialogPortal>
  )
}
